import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import format from 'string-template';
import { Button } from '@mui/material';
import { DatePicker } from '@mui/lab';

import DialogModal from '../../../../../../components/DialogModal';
import Promotion from '../../../../../Model/Promotion';
import LoggedInUserContext from '../../../../../../context/LoggedInUserContext';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import TextEditor from '../../../../../../components/TextEditor';
import useToast from '../../../../../hooks/useToast';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';

import {
  Container,
  Title,
  StyledTextField,
} from './styles';
import texts from './texts.json';

const PromotionModal = ({
  onClose,
  coachId,
  selectedPromotion,
}) => {
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState(moment().local().toDate());
  const [endDate, setEndDate] = useState(moment().local().add(1, 'day').toDate());
  const [isLoading, setIsLoading] = useState(false);
  const isComponentMountedRef = useComponentMounted();

  const { userId } = useContext(LoggedInUserContext);
  const { showToast } = useToast();

  const isEdit = !!selectedPromotion;

  useEffect(() => {
    if (isEdit) {
      setDescription(selectedPromotion.description);
      setStartDate(selectedPromotion.startDate);
      setEndDate(selectedPromotion.endDate);
    }
  }, [isEdit, selectedPromotion]);

  // Set end date to start date + 1 day if end date is before start date
  useEffect(() => {
    if (moment(endDate).isSameOrBefore(startDate)) {
      setEndDate(moment(startDate).add(1, 'day').toDate());
    }
  }, [endDate, startDate]);

  const handleSave = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!description) {
        throw new Error(texts.missingDescription);
      }

      const basePromotionData = {
        description,
        startDate: moment.utc(startDate).startOf('day').toDate(),
        endDate: moment.utc(endDate).startOf('day').toDate(),
      };

      if (isEdit) {
        await selectedPromotion.updateDoc({
          ...basePromotionData,
          updatedBy: userId,
          updatedAt: new Date(),
        });
      } else {
        await Promotion.createPromotion({
          ...basePromotionData,
          coachId,
          createdBy: userId,
          createdAt: new Date(),
          isDeleted: false,
        });
      }
      showToast(texts.promotionSavedSuccessfully);
      if (isComponentMountedRef.current) {
        onClose();
      }
    } catch (error) {
      showToast(format(texts.errorCreatingPromotion, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    coachId,
    description,
    endDate,
    isEdit,
    isComponentMountedRef,
    onClose,
    showToast,
    startDate,
    userId,
    selectedPromotion,
  ]);

  return (
    <>
      <DialogModal
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <Container>
          <Title>{isEdit ? texts.editPromotionTitle : texts.addPromotionTitle}</Title>
          <TextEditor
            value={description}
            onChange={setDescription}
          />
          <DatePicker
            label={texts.startDateLabel}
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            minDate={moment().local()}
            renderInput={(params) => (
              <StyledTextField {...params} />
            )}
          />
          <DatePicker
            label={texts.endDateLabel}
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            minDate={moment(startDate).add(1, 'day')}
            renderInput={(params) => (
              <StyledTextField {...params} />
            )}
          />
          <Button
            onClick={handleSave}
            variant="contained"
          >
            {texts.save}
          </Button>
        </Container>
      </DialogModal>
      <LoadingOverlay isLoading={isLoading} />
    </>
  );
};

PromotionModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  coachId: PropTypes.string.isRequired,
  selectedPromotion: PropTypes.object,
};

PromotionModal.defaultProps = {
  selectedPromotion: null,
};

export default PromotionModal;
